import type { Jsonified } from "@redotech/json/json";
import {
  AdminTreatmentType,
  baseSplitZodSchema,
  TreatmentType,
} from "@redotech/redo-ab-testing-common/split";
import type { CoverageMlModelDb } from "@redotech/redo-return-coverage-common/coverage-calculator/coverage-ml/registry-schema";
import type {
  CoverageCreditIncentive,
  DynamicReturnPricing,
  InternationalReturnPricing,
  PackageProtectionPricePerItemPricing,
  PackageProtectionPricingStrategy,
  PricingRuleSet,
} from "@redotech/redo-return-coverage-common/coverage-types";
import {
  ExtendedWarrantyBlurbType,
  ExtendedWarrantyDefaultSelection,
  ExtendedWarrantyPdpExperience,
  ExtendedWarrantyPdpNoCoverageType,
  ExtendedWarrantyRadioOptionType,
  ExtendedWarrantyTilesSize,
} from "@redotech/redo-return-warranties-common/extended-warranty";
import {
  ExtendedWarrantyCartToggleExperience,
  ExtendedWarrantyPdpRenderMethod,
  ExtendedWarrantyPopupExperience,
} from "@redotech/redo-return-warranties-common/extended-warranty-enable-criteria";
import type { AttachmentStrategy } from "@redotech/redo-storefront-common/attachment-strategy";
import { Icons } from "@redotech/redo-storefront-common/checkout-ui-customization";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import type { ToggleFields } from "@redotech/redo-storefront-common/toggle-fields";
import {
  ClickStandardCheckoutButton,
  TermsAndConditions,
} from "@redotech/redo-storefront-common/widget-config";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import type { ObjectId } from "bson";
import * as z from "zod";
import { WidgetConfig } from "./widget";

export type Treatment = Jsonified<TreatmentDoc>;

export interface FinalSaleReturnsPricing {
  minPrice: number;
  maxPrice: number;
  percentage: number;
  shippingLabelCoveragePrice?: number;
  pricingRuleSet: PricingRuleSet[];
}

export interface TreatmentDoc {
  _id: ObjectId | string;
  split_id: ObjectId;
  name: string;
  active: boolean;
  settings: {
    redoEnabled: boolean;
    returnEnabled: boolean;
    packageProtectionEnabled: boolean;
    attachmentStrategy: AttachmentStrategy;
    toggleTextOptions?: {
      returnToggle: ToggleFields;
      packageProtectionToggle: ToggleFields;
      bothProductToggle: ToggleFields;
    };
    hideOnPDP: boolean;
    redoAutoEnable: boolean;
    packageProtectionAutoEnable: boolean;
    hideBranding: boolean;
    hideCartProduct?: boolean;
    hideCartProductSelector?: string;
    hideCheckoutButtonSelector?: string;
    showIcons?: boolean;
    cartElementPlacementSelector?: string;
    cartElementShadowRootPlacementSelector?: string;
    cartListenerSelector?: string;
    cartElementPlacement?: InsertionPosition;
    cartElementImage?: URL;
    usingCartAndCheckoutToggle?: boolean;
    forceDoubleCheckoutToggle?: boolean;
    hideCheckoutUiWhenOptedIn?: boolean;
    customToggleCss?: string;
    displayIcon?: boolean;
    icon?: Icons;
    iconColor?: string;
    iconBackgroundColor?: string;
    returnOptions?: {
      pricePerOrder: string;
      serviceCountries: string[];
      dynamicPricing?: DynamicReturnPricing;
      internationalPricing?: InternationalReturnPricing;
      excludeCoverageIfAnyInvalidProduct: boolean;
    };
    widgetConfigs?: WidgetConfig[];
    groupNearbyToggleElementsTogether?: boolean;
    packageProtectionPricingRulesets?: PricingRuleSet[];
    packageProtectionMinPrice?: string;
    packageProtectionMaxPrice?: string;
    packageProtectionPricingStrategy?: PackageProtectionPricingStrategy;
    packageProtectionPricePerItem?: PackageProtectionPricePerItemPricing;
    finalSaleReturnsEnabled?: boolean;
    finalSaleReturnsPricing?: FinalSaleReturnsPricing;
    splitProducts?: boolean;
    termsAndConditions?: TermsAndConditions;
    clickStandardCheckoutButton?: ClickStandardCheckoutButton;
    coverageCreditIncentive?: CoverageCreditIncentive;
    lineItemRemoveOverrideSettings?: {
      enabled: boolean;
      showOnAllItems: boolean;
      buttonText: string;
    };
    packageProtectionCoverageMlModel?: CoverageMlModelDb;
  };
  weight: number;
}

export interface PopulatedSplit {
  _id: string;
  name?: string;
  team: string;
  active: boolean;
  treatments: Treatment[];
  startDate: string;
  endDate?: string | null;
  deleted?: boolean;
  treatmentType: AdminTreatmentType;
  rootSplitId?: string;
  parentSplitId?: string;
  generation?: number;
  concluded?: { selectedTreatmentId: string };
}

export const coverageSplitZodSchema = baseSplitZodSchema.extend({
  rootSplitId: zExt.objectId().optional(),
  parentSplitId: zExt.objectId().optional(),
  generation: z.number().nonnegative().optional(),
  concluded: z.object({ selectedTreatmentId: zExt.objectId() }).optional(),
});
export type CoverageSplitType = z.infer<typeof coverageSplitZodSchema>;

export const discountProclivity = [
  "very_conservative",
  "conservative",
  "balanced",
  "aggressive",
  "very_aggressive",
] as const;
export type DiscountProclivity = (typeof discountProclivity)[number];

export const marketingAgentSplitZodSchema = baseSplitZodSchema.extend({
  discountId: zExt.objectId().optional(),
  cooldownHours: z.number().positive(),
  discountProclivity: z.enum(discountProclivity).default("balanced"),
  klaviyoTagId: z.string().optional(),
  klaviyoOriginalFlowIds: z.array(z.string()).optional(),
  klaviyoDuplicatedFlowIds: z.array(z.string()).optional(),
});
export type MarketingAgentSplitType = z.infer<
  typeof marketingAgentSplitZodSchema
>;

export const shopperAgentSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
  createdAt: z.date(),
  attributionWindowDays: z.number().int().positive().default(90),
});
export type ShopperAgentSplitType = z.infer<typeof shopperAgentSplitZodSchema>;

export const expandedWarrantyTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  type: z.literal(TreatmentType.EXPANDED_WARRANTY),
  split_id: zExt.objectId(),
  name: z.string(),
  weight: z.number(),
  active: z.boolean(),
  settings: z.object({ expandedWarrantiesEnabled: z.boolean().optional() }),
  ewWidgetConfig: z.object({
    variantSelector: z.string().optional(),
    variantSelectorInsertPosition: createEnum(InsertionPosition, {
      enumName: "InsertionPosition",
    }).optional(),
    customToggleCss: z.string().optional(),
    cartToggleExperience: createEnum(ExtendedWarrantyCartToggleExperience, {
      enumName: "ExtendedWarrantyCartToggleExperience",
    }).optional(),
    cartToggleModalNotAddedDescription: z.string().optional(),
    cartToggleModalAddedDescription: z.string().optional(),
    cartToggleAddedDescription: z.string().optional(),
    cartToggleNotAddedDescription: z.string().optional(),
    clickListenerSelector: z.string().optional(),
    customPdpCss: z.string().optional(),
    pdpExperience: createEnum(ExtendedWarrantyPdpExperience, {
      enumName: "ExtendedWarrantyPdpExperience",
    }).optional(),
    pdpDefaultSelection: createEnum(ExtendedWarrantyDefaultSelection, {
      enumName: "ExtendedWarrantyDefaultSelection",
    }).optional(),
    pdpRenderMethod: createEnum(ExtendedWarrantyPdpRenderMethod, {
      enumName: "ExtendedWarrantyPdpRenderMethod",
    }).optional(),
    pdpNoCoverageType: createEnum(ExtendedWarrantyPdpNoCoverageType, {
      enumName: "ExtendedWarrantyPdpNoCoverageType",
    }).optional(),
    pdpNoCoveragePrice: z.string().optional(),
    pdpSelector: z.string().optional(),
    pdpTitle: z.string().optional(),
    pdpLabel: z.string().optional(),
    pdpCompactBlurbTitle: z.string().optional(),
    pdpTilesSize: createEnum(ExtendedWarrantyTilesSize, {
      enumName: "ExtendedWarrantyTilesSize",
    }).optional(),
    pdpBlurbType: createEnum(ExtendedWarrantyBlurbType, {
      enumName: "ExtendedWarrantyBlurbType",
    }).optional(),
    pdpCheckboxTitle: z.string().optional(),
    pdpRadioOptionType: createEnum(ExtendedWarrantyRadioOptionType, {
      enumName: "ExtendedWarrantyRadioOptionType",
    }).optional(),
    pdpRadioOptionTitle: z.string().optional(),
    pdpCoverageDisabledLabel: z.string().optional(),
    pdpDescription: z.string().optional(),
    pdpAddToCartDescription: z.string().optional(),
    pdpAddedToCartTitle: z.string().optional(),
    pdpAddedToCartDescription: z.string().optional(),
    pdpTilesBackdropEnabled: z.boolean().optional(),
    pdpInsertPosition: createEnum(InsertionPosition, {
      enumName: "InsertionPosition",
    }).optional(),
    customModalCss: z.string().optional(),
    logo: z.string().nullable().optional(),
    sideImage: z.string().nullable().optional(),
    modalTitle: z.string().optional(),
    modalCoverageHeader: z.string().optional(),
    modalDescription: z.string().optional(),
    modalPlanHeader: z.string().optional(),
    modalBulletPoints: z.array(z.string()).optional(),
    modalSeeDetailsUrl: z.string().optional(),
    popupExperience: createEnum(ExtendedWarrantyPopupExperience, {
      enumName: "ExtendedWarrantyPopupExperience",
    }).optional(),
    popupClickListenerSelector: z.string().optional(),
    popupFoundSelector: z.string().optional(),
    popupTitle: z.string().optional(),
    popupTitleSubtext: z.string().optional(),
    popupDescription: z.string().optional(),
    popupButtonAccept: z.string().optional(),
    popupButtonDecline: z.string().optional(),
    customPopupCss: z.string().optional(),
  }),
});

export type ExpandedWarrantyTreatment = z.infer<
  typeof expandedWarrantyTreatmentZodSchema
>;

export const signupFormSplitZodSchema = baseSplitZodSchema.extend({
  name: z.string(),
});
export type SignupFormSplitType = z.infer<typeof signupFormSplitZodSchema>;

export const signupFormTreatmentZodSchema = z.object({
  _id: zExt.objectId(),
  split_id: zExt.objectId(),
  name: z.string(),
  weight: z.number(),
  active: z.boolean(),
  formId: zExt.objectId(),
});

export type SignupFormTreatment = z.infer<typeof signupFormTreatmentZodSchema>;

export const automationOutreachStepTypes = [
  "send_email",
  "send_sms",
  "recover",
] as const;
export type AutomationOutreachStepType =
  (typeof automationOutreachStepTypes)[number];

const automationOutreachSplitZodSchema_ = baseSplitZodSchema.extend({
  name: z.string(),
  description: z.string().optional(),
  createdAt: z.date(),
  automationId: zExt.objectId(),
  automationStepId: zExt.objectId(),
  stepType: z.enum(automationOutreachStepTypes),
  originalTemplateId: zExt.objectId().optional(),
});
export const automationOutreachSplitZodSchema: z.ZodType<AutomationOutreachSplitType> =
  automationOutreachSplitZodSchema_;
export interface AutomationOutreachSplitType extends z.infer<
  typeof automationOutreachSplitZodSchema_
> {}

const automationOutreachTreatmentZodSchema_ = z.object({
  _id: zExt.objectId(),
  splitId: zExt.objectId(),
  templateName: z.string(),
  buckets: z.array(z.number().min(0).max(999)),
  isControl: z.boolean().default(false),
  templateId: zExt.objectId().optional(),
});
export const automationOutreachTreatmentZodSchema: z.ZodType<AutomationOutreachTreatment> =
  automationOutreachTreatmentZodSchema_;
export interface AutomationOutreachTreatment extends z.infer<
  typeof automationOutreachTreatmentZodSchema_
> {}

export const treatmentZodSchema = z.discriminatedUnion("type", [
  expandedWarrantyTreatmentZodSchema,
]);

export type TreatmentUnion = z.infer<typeof treatmentZodSchema>;
